import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SCROLL_MARK_TYPE,
  PRESENT_COLOR,
  SELECTION_COLOR,
  PAGENUMBER_MARK_TYPE,
} from '../constants';
import logger from 'utils/logger';

export const useMarks = (
  viewerControl: any,
  isPresenting: boolean,
  isTheatreMode: boolean,
  pauseCreateMarkHandlerRef: React.MutableRefObject<boolean>,
  pauseCreateMarkHandlerRef2: React.MutableRefObject<boolean>,
) => {
  const [markToBeCreated, setMarkToBeCreated] = useState<any>();
  const [markToBeUpdated, _setMarkToBeUpdated] = useState<any>();

  const pauseRemovedMarkHandlerRef = useRef<any>(null);

  const oldMarkToBeUpdatedColorRef = useRef<any>(null);

  const setMarkToBeUpdated = (mark: any) => {
    _setMarkToBeUpdated((prev: any) => {
      if (prev !== mark) {
        if (mark) {
          oldMarkToBeUpdatedColorRef.current = mark.fillColor;
          mark.fillColor = SELECTION_COLOR;
        } else {
          prev.fillColor = oldMarkToBeUpdatedColorRef.current;
          oldMarkToBeUpdatedColorRef.current = null;
        }
        return mark;
      } else {
        return prev;
      }
    });
  };

  const deleteMarkToBeCreated = useCallback(() => {
    try {
      pauseRemovedMarkHandlerRef.current = true;

      if (markToBeCreated) {
        setMarkToBeCreated(null);
        try {
          // Check if the mark still exists in the document before attempting any operations
          const allMarks = viewerControl.getAllMarks();
          const markExists = allMarks.some((m: any) => m.uid === markToBeCreated.uid);

          if (!markExists) {
            // Mark doesn't exist anymore, just clear the state
            return;
          }

          // For markButtonTrigger marks, try to clean subType and delete them
          const isMarkButtonTrigger =
            markToBeCreated.getData && markToBeCreated.getData('subType') === 'markButtonTrigger';

          if (isMarkButtonTrigger) {
            try {
              // Remove subType from the mark's data before deletion
              if (markToBeCreated.setData) {
                // Get current data and remove subType
                const currentData = markToBeCreated.data || {};
                if (currentData.subType) {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { subType, ...dataWithoutSubType } = currentData;
                  // Clear all data first, then set clean data
                  Object.keys(currentData).forEach((key) => {
                    if (key === 'subType') {
                      markToBeCreated.removeData(key);
                    }
                  });
                  // Set clean data back
                  Object.keys(dataWithoutSubType).forEach((key) => {
                    markToBeCreated.setData(key, dataWithoutSubType[key]);
                  });
                }
              }

              // Now try to delete the mark
              viewerControl.deleteMarks([markToBeCreated]);
            } catch (deleteError) {
              console.warn('Error deleting markButtonTrigger mark (non-critical):', deleteError, {
                markId: markToBeCreated.uid,
              });
            }
            return;
          }

          // For regular marks, delete normally
          viewerControl.deleteMarks([markToBeCreated]);
        } catch (e) {
          logger.ERROR(e);
        }
      }
    } finally {
      setTimeout(() => {
        pauseRemovedMarkHandlerRef.current = false;
      }, 250);
    }
  }, [markToBeCreated, viewerControl]);

  const clearAllMarkButtonTriggers = useCallback(() => {
    try {
      if (viewerControl && viewerControl.getAllMarks) {
        const allMarks = viewerControl.getAllMarks();
        const markButtonTriggerMarks = allMarks.filter((mark: any) => {
          try {
            return mark.getData && mark.getData('subType') === 'markButtonTrigger';
          } catch {
            return false;
          }
        });

        if (markButtonTriggerMarks.length > 0) {
          // Force hide mark buttons immediately before deleting marks
          markButtonTriggerMarks.forEach((mark: any) => {
            try {
              const event = new CustomEvent('forceHideMarkButtons', {
                detail: { markId: mark.uid },
              });
              window.dispatchEvent(event);
            } catch {
              console.warn('Error dispatching hide event for mark:', mark.uid);
            }
          });

          viewerControl.deleteMarks(markButtonTriggerMarks);
        }
      }

      // Also clear the state if the current mark is a markButtonTrigger
      if (
        markToBeCreated &&
        markToBeCreated.getData &&
        markToBeCreated.getData('subType') === 'markButtonTrigger'
      ) {
        setMarkToBeCreated(null);
      }
      if (
        markToBeUpdated &&
        markToBeUpdated.getData &&
        markToBeUpdated.getData('subType') === 'markButtonTrigger'
      ) {
        setMarkToBeUpdated(null);
      }
    } catch (error) {
      console.warn('Error clearing markButtonTrigger marks:', error);
    }
  }, [viewerControl, markToBeCreated, markToBeUpdated]);

  const markCreatedHandler = useCallback(
    ({ mark }: any) => {
      if (pauseCreateMarkHandlerRef.current || pauseCreateMarkHandlerRef2.current) return;

      const isScrollMark = mark.type === SCROLL_MARK_TYPE;
      const isPageNumberMark = mark.type === PAGENUMBER_MARK_TYPE;
      const isPresentMark = mark.fillColor === PRESENT_COLOR;
      const isMarkButtonTrigger = mark.getData && mark.getData('subType') === 'markButtonTrigger';

      if (
        (!isPresenting && !isTheatreMode) ||
        (isTheatreMode && !isPresentMark && !isScrollMark && !isPageNumberMark)
      ) {
        // Don't automatically set markButtonTrigger marks as markToBeCreated
        // They will be handled by the click handler instead
        if (isMarkButtonTrigger) {
          return;
        }
        setMarkToBeCreated((prev: any) => {
          if (prev !== mark) {
            setMarkToBeUpdated(undefined);
            return mark;
          } else {
            return prev;
          }
        });
      }
    },
    [isPresenting, isTheatreMode, pauseCreateMarkHandlerRef, pauseCreateMarkHandlerRef2],
  );

  useEffect(() => {
    viewerControl?.on('MarkCreated', markCreatedHandler);

    return () => {
      viewerControl?.off('MarkCreated', markCreatedHandler);
    };
  }, [markCreatedHandler, viewerControl]);

  return {
    deleteMarkToBeCreated,
    setMarkToBeUpdated,
    setMarkToBeCreated,
    markToBeUpdated,
    markToBeCreated,
    clearAllMarkButtonTriggers,
  };
};
